import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";

export const OtpVerify = ({ challenge, onVerified, verifyPath = "/auth/verify-otp", resendPath = "/auth/request-otp", extra, onChallenge }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(60);
  const [cid, setCid] = useState(challenge.challenge_id);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (value = code) => {
    if (value.length !== 6) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.post(verifyPath, { challenge_id: cid, email: challenge.email, code: value, ...(extra || {}) });
      onVerified(data, { challenge_id: cid, code: value });
    } catch (e) {
      setError(errMsg(e)); setCode("");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post(resendPath, { email: challenge.email });
      setCid(data.challenge_id); onChallenge?.(data.challenge_id);
      setCooldown(60); setError(""); toast.success("A new code has been sent if the email is registered.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="otp-verify">
      <p className="text-[#A3ADA3] text-sm">Enter the 6-digit code sent to <span className="text-white font-medium" data-testid="otp-email">{challenge.email}</span>. It expires in 10 minutes.</p>
      <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 6) verify(v); }} data-testid="otp-input" autoFocus>
        <InputOTPGroup className="gap-1.5 sm:gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-10 sm:h-14 sm:w-12 text-xl font-mono rounded-lg border border-[#2A362A] bg-[#080A08] first:rounded-l-lg last:rounded-r-lg" />)}
        </InputOTPGroup>
      </InputOTP>
      {error && <p className="text-sm text-red-400" data-testid="otp-error">{error}</p>}
      <button onClick={() => verify()} disabled={loading || code.length !== 6} data-testid="otp-submit-btn" className="w-full py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold flex justify-center disabled:opacity-50 hover:bg-[#c9ff6a] transition-colors">
        {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify Email"}
      </button>
      <button onClick={resend} disabled={cooldown > 0} data-testid="otp-resend-btn" className="w-full text-sm text-[#A3ADA3] hover:text-[#B7FF3C] disabled:opacity-50 disabled:hover:text-[#A3ADA3]">
        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
      </button>
    </div>
  );
};
